import React from 'react';

import checkMarkIcon from '@assets/img/icons/check-mark.svg';
import { Button } from '@components/Button';
import { ActionableDialog } from '@components/Dialog';
import { HorizontalSpace } from '@components/HorizontalSpace';
import { VerticalSpace } from '@components/VerticalSpace';
import MUIIconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';

const strings = createStrings({
  ja: {
    allTheContentsCanBeUsedForFree: '提供される全てのコンテンツは無料でご利用いただけます!',
    functionsForFreeAccount: '無料アカウントで利用できる機能',
    functionsForMembers: 'D2D MEMBERS申込後に利用できる機能',
    priceSimulatorForFree: '料金シミュレーター（1日10回）',
    priceSimulatorForMembers: '料金シミュレーター（無制限）',
    addressBook: '荷送人・納品先アドレス帳',
    commodityRegistration: '商品カタログ登録',
    bookSharing: '予約共有',
    tryFreeVersion: '無料版を使ってみる',
    cargoTracking: '貨物追跡',
    applyForMembersNow: '今すぐD2D MEMBERSに申込',
    bookingInquiry: '予約申込',
    bookingManagement: '案件管理',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  dialog: {
    maxWidth: '960px !important',
  },
  dialogTitle: {
    ...common.textAlignCenter,
    ...common.fontWeight700,
    fontSize: '30px',
    lineHeight: '45px',
    color: theme.palette.accent.dark,
  },
  dialogSubTitle: {
    ...common.textAlignCenter,
    ...common.biggerText,
    ...common.fontWeight700,
    lineHeight: '30px',
    color: theme.palette.accent.dark,
  },
  dialogContent: {
    backgroundColor: theme.palette.grey[100],
    height: '461px',
    position: 'relative',
    ...common.flexInline,
  },
  closeButton: {
    position: 'absolute',
    right: 0,
  },
  dialogSplitVertical: {
    position: 'absolute',
    width: 1,
    top: 24,
    bottom: 36,
    left: '50%',
    right: '50%',
    zIndex: 10,
    backgroundColor: theme.palette.grey[300],
  },
  dialogContentColumnOuter: {
    flexBasis: 0,
    flex: 1,
    padding: '24px 0',
  },
  dialogContentLeftColumn: {
    marginLeft: '68px',
    rowGap: '10px',
  },
  dialogContentRightColumn: {
    marginLeft: '53px',
  },
  dialogCheckItem: {
    ...common.flexInline,
  },
  dialogCheckList: {
    margin: '25px 0',
    rowGap: '10px',
    height: '249px',
    ...common.flexColumn,
  },
  button: {
    height: '56px !important',
    width: '340px !important',
    borderRadius: '4px !important',
    fontWeight: 700,
    fontSize: '18px',
    lineHeight: '20px',
  },
}));

interface CheckItemProps {
  text: string;
}

const CheckItem: React.FC<CheckItemProps> = (props) => {
  const { text } = props;

  const classes = useStyles();

  return (
    <div className={classes.dialogCheckItem}>
      <img src={checkMarkIcon} />
      <HorizontalSpace width={10} />
      <div>{text}</div>
    </div>
  );
};

interface MemberIntroDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export const MemberIntroDialog: React.FC<MemberIntroDialogProps> = (props) => {
  const classes = { ...useStyles(), ...useCommonStyles() };
  const history = useHistory();
  const { isOpen, setIsOpen } = props;

  return (
    <ActionableDialog open={isOpen} classes={{ paper: classes.dialog }}>
      <MUIIconButton className={classes.closeButton} onClick={() => setIsOpen(false)}>
        <CloseIcon />
      </MUIIconButton>
      <VerticalSpace height={25} />
      <div className={classes.dialogTitle}>D2D MEMBERS</div>
      <VerticalSpace height={10} />
      <div className={classes.dialogSubTitle}>{strings.allTheContentsCanBeUsedForFree}</div>
      <VerticalSpace height={29} />
      <div className={classes.dialogContent}>
        <div className={classes.dialogSplitVertical} />
        <div className={classes.dialogContentColumnOuter}>
          <div className={classes.dialogContentLeftColumn}>
            <div className={clsx(classes.biggerText, classes.fontWeight700)}>
              {strings.functionsForFreeAccount}
            </div>
            <div className={classes.dialogCheckList}>
              <CheckItem text={strings.priceSimulatorForFree} />
              <CheckItem text={strings.cargoTracking} />
            </div>
            <Button
              className={classes.button}
              color={'accent-outlined'}
              text={strings.tryFreeVersion}
              type="button"
              onClick={() => setIsOpen(false)}
            />
          </div>
        </div>
        <div className={classes.dialogContentColumnOuter}>
          <div className={classes.dialogContentRightColumn}>
            <div className={clsx(classes.biggerText, classes.fontWeight700)}>
              {strings.functionsForMembers}
            </div>
            <div className={classes.dialogCheckList}>
              <CheckItem text={strings.priceSimulatorForMembers} />
              <CheckItem text={strings.cargoTracking} />
              <CheckItem text={strings.bookingInquiry} />
              <CheckItem text={strings.bookingManagement} />
              <CheckItem text={strings.addressBook} />
              <CheckItem text={strings.commodityRegistration} />
              <CheckItem text={strings.bookSharing} />
            </div>
            <Button
              className={classes.button}
              color={'accent'}
              text={strings.applyForMembersNow}
              type="button"
              onClick={() => history.push(url('/utilities/MembershipForm/PolicyAgreement'))}
            />
          </div>
        </div>
      </div>
    </ActionableDialog>
  );
};
